import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { adminApi } from '../utils';

// Photo Moderation Panel Component
const PhotoModerationPanel = ({ token }) => {
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  const fetchPendingPhotos = useCallback(async () => {
    try {
      const response = await adminApi.get('/admin/pending-photos');
      setPendingPhotos(response.data.pending_photos || []);
    } catch (error) {
      console.error('Error fetching pending photos:', error);
      toast.error('Failed to load pending photos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPhotos();
  }, [fetchPendingPhotos]);

  const handleApprove = async (reviewId, photoIndex) => {
    const key = `${reviewId}-${photoIndex}`;
    setProcessing(prev => ({ ...prev, [key]: 'approving' }));
    try {
      await adminApi.post(`/admin/photos/approve?review_id=${reviewId}&photo_index=${photoIndex}`);
      toast.success('Photo approved!');
      setPendingPhotos(prev => prev.filter(p => !(p.review_id === reviewId && p.photo_index === photoIndex)));
    } catch (error) {
      toast.error('Failed to approve photo');
    } finally {
      setProcessing(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleReject = async (reviewId, photoIndex) => {
    const key = `${reviewId}-${photoIndex}`;
    setProcessing(prev => ({ ...prev, [key]: 'rejecting' }));
    try {
      await adminApi.post(`/admin/photos/reject?review_id=${reviewId}&photo_index=${photoIndex}`);
      toast.success('Photo rejected and removed');
      setPendingPhotos(prev => prev.filter(p => !(p.review_id === reviewId && p.photo_index === photoIndex)));
    } catch (error) {
      toast.error('Failed to reject photo');
    } finally {
      setProcessing(prev => ({ ...prev, [key]: null }));
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading pending photos...</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Photo Moderation</h2>

      {pendingPhotos.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
          <p className="text-lg font-medium">All caught up!</p>
          <p className="text-muted-foreground">No photos pending approval.</p>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pendingPhotos.map((photo) => {
            const key = `${photo.review_id}-${photo.photo_index}`;
            const isProcessing = processing[key];
            const photoUrl = photo.photo_url?.startsWith('http') 
              ? photo.photo_url 
              : photo.photo_url;
            
            return (
              <Card key={key} className="overflow-hidden" data-testid={`pending-photo-${key}`}>
                <div className="aspect-video bg-gray-100 relative">
                  <img
                    src={photoUrl}
                    alt="Review photo pending approval"
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="p-4 space-y-3">
                  <div className="space-y-1">
                    <p className="font-medium text-sm truncate">{photo.product_name}</p>
                    <p className="text-xs text-muted-foreground">Review: &quot;{photo.review_title}&quot;</p>
                    <p className="text-xs text-muted-foreground">By: {photo.reviewer_email}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleApprove(photo.review_id, photo.photo_index)}
                      disabled={!!isProcessing}
                      data-testid={`approve-${key}`}
                    >
                      {isProcessing === 'approving' ? 'Approving...' : (
                        <><CheckCircle className="w-4 h-4 mr-1" /> Approve</>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => handleReject(photo.review_id, photo.photo_index)}
                      disabled={!!isProcessing}
                      data-testid={`reject-${key}`}
                    >
                      {isProcessing === 'rejecting' ? 'Rejecting...' : (
                        <><XCircle className="w-4 h-4 mr-1" /> Reject</>
                      )}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PhotoModerationPanel;
